// Generic "pick one or more saved medications" modal. Extracted from
// AddMedToSuggestionModal so the SuggestionCard "+ Add another medication" flow and
// the Missed-doses review's "+ Add another medication" flow share one picker (search,
// As needed / Scheduled sections, opt-in multi-select) and differ only in what they do
// with the picked medications (`onSelect`).

import { useMemo, useState } from "react";
import { ApiError } from "../lib/api.js";
import { matchesMedicationSearch } from "../lib/conditionSynonyms.js";
import { PillIcon } from "./icons.js";
import { ModalShell } from "./ModalShell.js";
import { useMedChoices, MedSection, type MedChoice } from "./medPicker.js";
import { ClearableInput } from "./ClearableInput.js";

/** Progress for a multi-med `onSelect` that works through the picks one at a time. */
export interface MedPickerProgress {
  name: string;
  /** 1-based position of the medication currently in flight. */
  current: number;
  total: number;
}

export function MedPickerModal({
  patientId,
  title = "Add another medication",
  excludeNames,
  includeOverdose = true,
  onSelect,
  onClose,
  onError,
}: {
  patientId: string;
  title?: string;
  /** Lowercased names to leave out of the list (already present where they're going). */
  excludeNames: Set<string>;
  /** false = no server overdose check and no red flags (backfilled, past-dated doses). */
  includeOverdose?: boolean;
  /** Called with the picked medications. May be async; the modal shows a spinner until it
   *  settles, then closes. Throw to keep the modal open and surface `onError`. */
  onSelect: (meds: MedChoice[], report: (p: MedPickerProgress) => void) => void | Promise<void>;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { choices, loading, error: loadError } = useMedChoices(patientId, includeOverdose);
  const [query, setQuery] = useState("");
  // Progress of an in-flight onSelect, or null when idle. Non-null both drives `busy`
  // and lets the full-screen overlay say which medication is in flight, since a server
  // round trip per med can take a couple seconds and a bare list reads as a frozen app.
  const [progress, setProgress] = useState<MedPickerProgress | null>(null);
  const busy = progress !== null;

  // Multi-select mode, opt-in (mirrors QuickMedLog's dashboard "Log medication"
  // picker): off by default, keys are `${regimenId}:${lineId}`.
  const [multiMode, setMultiMode] = useState(false);
  const [multiSelected, setMultiSelected] = useState<Set<string>>(new Set());

  const available = useMemo(
    () => choices.filter((c) => !excludeNames.has(c.name.trim().toLowerCase())),
    [choices, excludeNames]
  );

  const filtered = useMemo(() => {
    const q = query.trim();
    return q ? available.filter((c) => matchesMedicationSearch(q, c.name, c.intendedFor)) : available;
  }, [available, query]);

  const asNeeded = filtered.filter((c) => c.fromAsNeeded);
  const scheduled = filtered.filter((c) => !c.fromAsNeeded);

  function toggleMultiMode() {
    setMultiMode((m) => !m);
    setMultiSelected(new Set());
  }

  function toggleMedInBatch(c: MedChoice) {
    const key = `${c.regimenId}:${c.lineId}`;
    setMultiSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function run(meds: MedChoice[]) {
    if (meds.length === 0) return;
    setProgress({ name: meds[0]!.name, current: 1, total: meds.length });
    try {
      await onSelect(meds, setProgress);
      onClose();
    } catch (err) {
      onError(
        err instanceof ApiError
          ? err.message
          : meds.length > 1
            ? "Couldn't add those medications"
            : "Couldn't add that medication"
      );
      setProgress(null);
    }
  }

  function addBatch() {
    // Click order (Set insertion order), as the suggestion-card flow always did.
    const picked = [...multiSelected]
      .map((key) => available.find((c) => `${c.regimenId}:${c.lineId}` === key))
      .filter((c): c is MedChoice => !!c);
    void run(picked);
  }

  return (
    <ModalShell
      title={title}
      icon={<PillIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      subheader={
        !loading && !busy && available.length > 0 ? (
          <ClearableInput
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or what it's for…"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
        ) : undefined
      }
      bodyClassName="overflow-y-auto p-4 h-[50vh]"
    >
      {busy ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-(--accent) dark:border-gray-700"
            aria-hidden="true"
          />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Adding {progress.name}
            {progress.total > 1 ? ` (${progress.current} of ${progress.total})` : ""}…
          </p>
        </div>
      ) : (
        <>
          {loadError && (
            <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {loadError}
            </p>
          )}
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading medications…</p>
          ) : available.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-600 dark:text-gray-300">
              No other saved medications to add.
            </p>
          ) : (
            <>
              <div className="mb-2 flex justify-end">
                <button
                  onClick={toggleMultiMode}
                  className="text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {multiMode ? "Cancel" : "Select multiple"}
                </button>
              </div>
              {filtered.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">No matches.</p>
              )}
              {asNeeded.length > 0 &&
                (multiMode ? (
                  <MedSection title="As needed" meds={asNeeded} selected={multiSelected} onToggle={toggleMedInBatch} />
                ) : (
                  <MedSection title="As needed" meds={asNeeded} onPick={(c) => void run([c])} />
                ))}
              {scheduled.length > 0 &&
                (multiMode ? (
                  <MedSection title="Scheduled" meds={scheduled} selected={multiSelected} onToggle={toggleMedInBatch} />
                ) : (
                  <MedSection title="Scheduled" meds={scheduled} onPick={(c) => void run([c])} />
                ))}
              {multiMode && multiSelected.size > 0 && (
                <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3">
                  <button
                    onClick={addBatch}
                    className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
                  >
                    {`Add (${multiSelected.size})`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </ModalShell>
  );
}
